/**
 * CheckoutForm.tsx
 *
 * Author: Victor Chevillotte <victor.chevillotte@ircamamplify.com>
 * Copyright © Ircam amplify 2025-2026
 */

import { logger } from '@/config/logger'
import { SubscriptionFrequency, TierType } from '@amplify3/duplo-back-end'
import {
  AddressElement,
  PaymentElement,
  useCheckout,
} from '@stripe/react-stripe-js'
import { checkVAT, countries } from 'jsvat'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

// Function to validate the tax ID using jsvat for EU VAT numbers.
const isValidTaxId = (taxId: string, taxIdType: string) => {
  if (taxIdType === 'eu_vat') {
    const result = checkVAT(taxId, countries)
    return result.isValid
  }
  // Add other validation logic for different tax ID types if needed.
  return true
}

// Component for collecting business tax information with improved styling.
const TaxIdCollectionForm = ({
  businessName,
  setBusinessName,
  taxIdValue,
  setTaxIdValue,
  setTaxIdType,
  isPurchaseAsBusiness,
  setIsPurchaseAsBusiness,
}: {
  businessName: string
  setBusinessName: any
  taxIdValue: string
  setTaxIdValue: any
  setTaxIdType: any
  isPurchaseAsBusiness: boolean
  setIsPurchaseAsBusiness: any
}) => {
  // Local state for managing tax info.
  const [isEditing, setIsEditing] = useState(true)

  const [errorMessage, setErrorMessage] = useState('')

  // Get updateTaxIdInfo and any existing taxIdInfo from checkout.
  const { taxIdInfo } = useCheckout()

  // Prefill fields if taxIdInfo is available.
  useEffect(() => {
    if (taxIdInfo) {
      if (taxIdInfo.taxId && taxIdInfo.taxId.value) {
        setTaxIdValue(taxIdInfo.taxId.value)
        setTaxIdType(taxIdInfo.taxId.type)
      }
      if (taxIdInfo.businessName) {
        setBusinessName(taxIdInfo.businessName)
      }
    }
  }, [taxIdInfo])

  // Handle change in the business purchase checkbox.
  const onIsPurchaseAsBusinessChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked
    setIsPurchaseAsBusiness(checked)
    if (!checked) {
      setErrorMessage('')
      setIsEditing(true)
    }
  }

  return (
    <div
      style={{
        background: '#f9f9f9',
        borderRadius: '8px',
        padding: '1rem',
        marginTop: '1rem',
      }}
    >
      <div>
        <label style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={isPurchaseAsBusiness}
            onChange={onIsPurchaseAsBusinessChange}
            style={{ marginRight: '8px' }}
          />
          Purchase as business
        </label>
      </div>

      {isPurchaseAsBusiness && (
        <>
          {isEditing ? (
            <>
              <div style={{ marginBottom: '1rem', marginTop: '1rem' }}>
                <label
                  htmlFor="businessName"
                  style={{ display: 'block', marginBottom: '4px' }}
                >
                  Business Name:
                </label>
                <input
                  type="text"
                  id="businessName"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Enter business name"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label
                  htmlFor="taxId"
                  style={{ display: 'block', marginBottom: '4px' }}
                >
                  Tax ID:
                </label>
                <input
                  type="text"
                  id="taxId"
                  value={taxIdValue}
                  onChange={(e) => setTaxIdValue(e.target.value)}
                  placeholder="Enter tax ID"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                  }}
                />
              </div>

              {errorMessage && (
                <div style={{ marginTop: '0.5rem', color: 'red' }}>
                  {errorMessage}
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                padding: '1rem',
                background: '#fff',
                borderRadius: '4px',
                border: '1px solid #ddd',
              }}
            >
              <div style={{ marginBottom: '0.5rem' }}>
                Business Name: <strong>{businessName}</strong>
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                Tax ID: <strong>{taxIdValue}</strong>
              </div>
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Edit
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

const TotalPrice = ({
  checkout,
  tier,
  subscriptionFrequency,
}: {
  checkout: any
  tier: TierType
  subscriptionFrequency: string | undefined
}) => {
  const getTotalText = (checkout: any): string => {
    let currencySymbol = ''

    if (checkout?.currency === 'usd') {
      currencySymbol = '$'
    } else if (checkout?.currency === 'eur') {
      currencySymbol = '€'
    } else {
      // Unknown currency: use it as it is, with a trailing space.
      currencySymbol = `${checkout.currency} `
    }

    if (tier === TierType.PayAsYouGo) {
      // The per-credit price for Pay As You Go plan must be hardcoded
      // Because the amount in unit_amount_decimal can't reprensant the
      // the actual price of one credit in Stripe.
      return `${currencySymbol}2 per credit`
    } else {
      // Prices in the Stripe data are stored in cents, not unit.
      return `${checkout.total.total.amount}${subscriptionFrequency ? ' ' + subscriptionFrequency : ''}`
    }
  }

  return <div>{checkout ? getTotalText(checkout) : 'Loading...'}</div>
}

interface DiscountAmount {
  amount: number
  currency: string
  displayName: string
  recurring?: {
    type: string
    durationInMonths: number
  }
}

const CheckoutForm = ({
  tier,
  planName,
  subscriptionFrequency,
  numberOfCreditsForSubscription,
  offPackageCreditCost,
  setIsloading,
}: {
  tier: TierType
  planName: string
  subscriptionFrequency: string | undefined
  numberOfCreditsForSubscription: number
  offPackageCreditCost: number
  setIsloading: (loading: boolean) => void
}) => {
  const checkout = useCheckout()

  const [businessName, setBusinessName] = useState('')
  const [taxIdType, setTaxIdType] = useState('eu_vat')
  const [taxIdValue, setTaxIdValue] = useState('')
  const [isPurchaseAsBusiness, setIsPurchaseAsBusiness] = useState(false)

  // Log checkout object for debugging.
  logger.info('checkout', checkout)
  if (!checkout.loading) {
    setIsloading(false)
  }
  logger.info(checkout.total.total.amount)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<any>(null)
  const [promoCode, setPromoCode] = useState('')
  const [promoMessage, setPromoMessage] = useState('')
  const [promoCodesApplied, setPromoCodesApplied] = useState<DiscountAmount[]>(
    []
  )

  /**
   *  Apply promo code handler.
   */
  const handleApplyPromoCode = () => {
    checkout
      .applyPromotionCode(promoCode)
      .then(
        (result: {
          type: string
          success: { discountAmounts: DiscountAmount[] }
        }) => {
          if (result.type === 'error') {
            setPromoMessage('Promo code is invalid or could not be applied.')
          } else {
            setPromoMessage('Promo code applied successfully!')
            setPromoCode('')
            // Merge previous discounts with the new ones
            setPromoCodesApplied((prev) => [
              ...prev,
              ...result.success.discountAmounts,
            ])
          }
        }
      )
  }

  /**
   * Get next end of month date.
   */
  const getNextEndOfMonthDate = (): string => {
    const invoiceDate = new Date()
    const lastDay = new Date(
      invoiceDate.getFullYear(),
      invoiceDate.getMonth() + 1,
      0
    ).getDate()
    return `${lastDay}/${invoiceDate.getMonth() + 1}/${invoiceDate.getFullYear()}`
  }

  /**
   * Get next invoice date.
   */
  const getNextInvoiceDate = (): string => {
    const invoiceDate = new Date()
    return invoiceDate.toLocaleDateString()
  }

  /**
   * Get invoice date message based on tier and subscription frequency.
   */
  const getInvoiceDateMessage = (
    tier: TierType,
    subscriptionFrequency: string | undefined
  ): string | undefined => {
    switch (tier) {
      case TierType.PayAsYouGo:
        return `You will be billed pro-rata until ${getNextEndOfMonthDate()}. After that, you'll be billed on the last day of each month.`
      case TierType.Subscription:
        switch (subscriptionFrequency) {
          case SubscriptionFrequency.Monthly:
            return `You will be charged for your first month today, on ${getNextInvoiceDate()}, and receive your credits. Afterwards, you will be billed monthly on your subscription anniversary date.`
          case SubscriptionFrequency.Yearly:
            return `With the annual commitment plan, your payment will be processed today (${getNextInvoiceDate()}), and you will receive all your credits immediately. You have until the same date next year to use them, after which your plan will automatically renew.`
          default:
            return undefined
        }
      default:
        return undefined
    }
  }

  // Validate inputs and update tax info in session.
  const validateAndUpdateTaxId = async () => {
    if (!isPurchaseAsBusiness) {
      return true
    }
    if (!businessName) {
      setError({ message: 'Please enter a business name.' })
      return false
    }
    if (!taxIdValue) {
      setError({ message: 'Please enter a tax ID.' })
      return false
    }

    // Validate the tax ID format using jsvat.
    if (!isValidTaxId(taxIdValue, taxIdType)) {
      setError({
        message:
          "Please enter a valid VAT number or untick the 'Purchase as business' option.",
      })
      return false
    }

    const res = await checkout.updateTaxIdInfo({
      taxId: { value: taxIdValue, type: taxIdType },
      businessName,
    })
    logger.info('Tax ID info updated:', res)
    return true
  }

  // Form submission handler.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    const res = await validateAndUpdateTaxId()
    if (!res) {
      setLoading(false)
      return
    }
    setError(null)

    // Parameters for the check-out
    // Add Payment method saving only if the total amount is non-null
    const checkoutParam =
      checkout?.total?.total.minorUnitsAmount > 0
        ? { savePaymentMethod: true }
        : {}

    // Check-out
    try {
      await checkout
        .confirm(checkoutParam)
        .then((result: { type: string; error: any }) => {
          if (result.type === 'error') {
            setError(result.error)
          }
          setLoading(false)
        })
    } catch (error) {
      setError(error)
      setLoading(false)
    }
  }

  // Styles for container and cards.
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2rem',
  }

  const columnStyle: React.CSSProperties = {
    flex: '1 1 300px',
  }

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    padding: '1.5rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '1rem',
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}
    >
      <div style={containerStyle}>
        {/* Order Summary Column */}
        <div style={columnStyle}>
          <div style={{ ...cardStyle, marginBottom: '1rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Billing Information</h3>
            <AddressElement options={{ mode: 'billing' }} />
            <TaxIdCollectionForm
              businessName={businessName}
              setBusinessName={setBusinessName}
              taxIdValue={taxIdValue}
              setTaxIdValue={setTaxIdValue}
              setTaxIdType={setTaxIdType}
              isPurchaseAsBusiness={isPurchaseAsBusiness}
              setIsPurchaseAsBusiness={setIsPurchaseAsBusiness}
            />
          </div>
        </div>

        {/* Payment Column */}
        <div style={columnStyle}>
          <div style={{ ...cardStyle, padding: '1rem' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2.1rem',
              }}
            >
              <h3 style={{ margin: 0 }}>Order Summary</h3>
              <Link
                to="/tierselection"
                style={{ textDecoration: 'underline', fontSize: '1rem' }}
              >
                Change Plan
              </Link>
            </div>

            {checkout.total && (
              <div style={{ marginTop: '1rem', paddingTop: '1rem' }}>
                {/* Main row: label + either Pay‑As‑You‑Go or the subscription box */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start', // align top of the box with the label
                    fontSize: '1.1rem',
                  }}
                >
                  <div style={{ fontWeight: 'bold' }}>Selected plan</div>

                  {tier === TierType.PayAsYouGo ? (
                    /* Simple text for Pay‑As‑You‑Go */
                    <span style={{ fontWeight: 'bold' }}>Pay As You Go</span>
                  ) : (
                    /* Box grouping Subscription + Off-package stacked */
                    <div
                      style={{
                        maxWidth: '540px',
                      }}
                    >
                      {/* Subscription line */}
                      <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                        {planName}{' '}
                        {subscriptionFrequency && (
                          <>
                            {subscriptionFrequency ===
                            SubscriptionFrequency.Monthly
                              ? 'Monthly'
                              : 'Yearly'}{' '}
                          </>
                        )}
                        <span>
                          {numberOfCreditsForSubscription} tracks
                          {subscriptionFrequency &&
                            ` / ${
                              subscriptionFrequency ===
                              SubscriptionFrequency.Monthly
                                ? 'month'
                                : 'year'
                            }`}
                        </span>
                      </div>

                      {/* Off‑package line below */}
                      {offPackageCreditCost && offPackageCreditCost > 0 ? (
                        <div
                          style={{
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            marginTop: '0.25rem',
                          }}
                        >
                          Off‑package
                          <span
                            style={{
                              fontSize: '0.9rem',
                              marginLeft: '0.5rem',
                              fontWeight: 'normal',
                            }}
                          >
                            {offPackageCreditCost} euros / credit
                          </span>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Total Summary */}
            {checkout.total && (
              <div
                style={{
                  borderTop: '1px solid #ddd',
                  marginTop: '2rem',
                  paddingTop: '0.5rem',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    marginTop: '0.5rem',
                  }}
                >
                  <span>Total</span>
                  <div
                    className="
                    w-1/2 text-right font-semibold text-[30px]
                    text-ircamprimary-accent
                  "
                  >
                    <TotalPrice
                      checkout={checkout}
                      tier={tier}
                      subscriptionFrequency={subscriptionFrequency}
                    />
                  </div>
                </div>
              </div>
            )}

            <div style={{ marginTop: '2rem', marginBottom: '1.3rem' }}>
              <p style={{ fontSize: '0.9rem', color: '#555' }}>
                {getInvoiceDateMessage(tier, subscriptionFrequency)}
              </p>
            </div>
          </div>

          <div style={{ ...cardStyle, marginBottom: '0.5rem' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1rem',
              }}
            >
              <h3 style={{ marginRight: '0.5rem' }}>Payment</h3>
              <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRek2EqBo5YIE0TPMVMlIFA594WZZeuqYdAQQ&s"
                alt="Stripe"
                style={{ height: '25px', marginTop: '7px' }}
              />
            </div>
            {/* Promo Code Section */}
            <div
              style={{
                marginTop: '1rem',
                marginBottom: '0.5rem',
                display: 'flex',
                gap: '0.5rem',
              }}
            >
              <style>
                {`
                input::placeholder {
                  color: #77787d;
                }
              `}
              </style>
              <input
                type="text"
                placeholder="Enter promo code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                style={{
                  flex: 1,
                  paddingLeft: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  maxWidth: '200px',
                  height: 30,
                }}
              />
              <button
                type="button"
                onClick={handleApplyPromoCode}
                style={{
                  padding: '0px 12px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Apply
              </button>
            </div>
            {promoMessage && (
              <p
                style={{
                  marginTop: '0rem',
                  marginBottom: '0.5rem',
                  color: 'grey',
                }}
              >
                {promoMessage}
              </p>
            )}
            {promoCodesApplied.map((promo: any, index: number) => (
              <p
                key={index}
                style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
              >
                {promo.displayName}: {promo.amount}{' '}
                {promo.recurring
                  ? promo.recurring.type === 'forever'
                    ? "off every invoice, for as long as you're subscribed."
                    : 'off every invoice, for your first ' +
                      promo.recurring.durationInMonths +
                      ' month' +
                      (promo.recurring.durationInMonths > 1 ? 's.' : '.')
                  : 'off your first invoice (one-time discount).'}
              </p>
            ))}
            <PaymentElement options={{ layout: 'accordion' }} />
          </div>
          {error && (
            <p style={{ color: 'red', marginTop: '0.5rem' }}>{error.message}</p>
          )}
          <button
            type="submit"
            className="w-full button button-large button-dark py-2 px-4 my-4"
            disabled={checkout.loading || loading}
          >
            <div className="button button-label">Pay</div>
          </button>
        </div>
      </div>
    </form>
  )
}

export default CheckoutForm
